import { getKeycloakInstance, setLogoutInProgress } from "@auth/keycloakClient";
import { isKeycloakAuth, isLegacyAuth } from "@configs/authMode";
import ToastConstant from "@configs/react-toastify/snackbar";
import { BASE_PATH_RUNTIME } from "@configs/runtimeConfig";
import { LOCAL_STORAGE, LOGIN_CONTEXT, TOAST, COOKIES, LOGGED_IN_STATUS } from "@constants/index";
import { removeLocalStorage } from "./local-storage";
import i18n from "i18next";
import { getCookieValue } from "./common";
import { clearAuthSession, logoutOnSessionExpired } from "./authSession";
import { basename } from "@router/index";

export { logoutOnSessionExpired } from "./authSession";

function getAuthApiBaseUrl() {
  const runtimeConfig = typeof window !== "undefined" ? window.__APP_CONFIG__ : null;
  const baseUrl = runtimeConfig?.API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  return baseUrl.replace(/\/+$/, "");
}

/**
 * POST /logout to delete this session's row from the DB.
 * Uses native fetch so callers avoid the Axios interceptor graph in apis/index.js.
 * Sends Bearer JWT because the endpoint requires @RequireAnyAppPermission.
 */
export async function postServerLogout(sessionId) {
  if (!sessionId) {
    return;
  }

  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${getAuthApiBaseUrl()}/logout`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    throw new Error(`Logout failed: ${response.status}`);
  }
}

function appBaseForRedirect() {
  const trimmed = (BASE_PATH_RUNTIME || "/").replace(/\/+$/, "");
  return trimmed === "/" ? "" : trimmed;
}

/**
 * Logs out user and clears auth state.
 * @param {boolean} isAutoLogout
 * @param {{
 *   silent?: boolean,
 *   clearIndexedDb?: boolean,
 *   clearPermissions?: Function,
 *   sessionExpired?: boolean,
 * }} options
 */
export const logout = async (isAutoLogout = false, options = {}) => {
  const {
    silent = false,
    clearIndexedDb = false,
    clearPermissions,
    sessionExpired = false,
  } = options;

  if (sessionExpired) {
    await logoutOnSessionExpired();
    return;
  }

  const kc = getKeycloakInstance();
  let keycloakLogoutStarted = false;

  setLogoutInProgress(true);

  try {
    if (!silent) {
      if (isAutoLogout) {
        ToastConstant(
          TOAST.success,
          i18n.t("toast.logout.success.title"),
          i18n.t("toast.logout.success.auto-logout-message"),
        );
      } else {
        ToastConstant(TOAST.success, i18n.t("toast.logout.success.title"), i18n.t("toast.logout.success.message"));
      }
    }

    if (isLegacyAuth()) {
      try {
        // Read sessionId + token before clearAuthSession removes them from localStorage.
        const sessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
        await postServerLogout(sessionId);
      } catch {
        /* session/token may already be invalid (e.g. inactivity after JWT expiry) */
      }
    }

    await clearAuthSession({ clearIndexedDb });
    if (typeof clearPermissions === "function") {
      clearPermissions();
    }

    if (isKeycloakAuth() && kc) {
      const base = appBaseForRedirect();
      const redirectUri = `${window.location.origin}${base}/login`.replace(/([^:]\/)\/+/g, "$1");
      keycloakLogoutStarted = true;
      await kc.logout({ redirectUri });
    }
  } finally {
    // Keycloak logout usually redirects/reloads the page; reset there (Login mount).
    // Legacy / failed Keycloak logout: clear the guard so login can proceed in SPA.
    if (!keycloakLogoutStarted) {
      setLogoutInProgress(false);
    }
  }
};

const forceLogoutWithInfoToast = async (titleKey, messageKey, clearPermissions) => {
  ToastConstant(TOAST.info, i18n.t(titleKey), i18n.t(messageKey));
  await new Promise((resolve) => setTimeout(resolve, 3000));
  await logout(false, {
    silent: true,
    clearIndexedDb: true,
    clearPermissions,
  });

  if (isKeycloakAuth()) {
    return;
  }
  
  const baseName = basename && basename !== "/" ? basename : "";
  const redirectPath = `${baseName}/login`.replace(/\/+/g, "/");
  window.location.href = redirectPath;
};

export const forceLogoutByAuthorizationChange = async (clearPermissions) => {
  await forceLogoutWithInfoToast(
    "toast.login.authorization-changed.title",
    "toast.login.authorization-changed.message",
    clearPermissions,
  );
};

export const forceLogoutByAccountInactive = async (clearPermissions) => {
  await forceLogoutWithInfoToast(
    "toast.login.account-inactive.title",
    "toast.login.account-inactive.message",
    clearPermissions,
  );
};

export const forceLogoutByPasswordReset = async (clearPermissions) => {
  await forceLogoutWithInfoToast(
    "toast.login.password-reset.title",
    "toast.login.password-reset.message",
    clearPermissions,
  );
};

export const isSuperuser = () => {
  const savedPermissions = localStorage.getItem(LOCAL_STORAGE.PERMISSIONS);
  return savedPermissions.includes(LOGIN_CONTEXT.SUPERUSER);
};

export const checkAuthenticationStatus = () => {
  if (getCookieValue(COOKIES.LOGIN_STATUS) !== LOGGED_IN_STATUS) {
    removeLocalStorage([LOCAL_STORAGE.TOKEN, LOCAL_STORAGE.USER_ID]);
    return false;
  }

  const userId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
  if (!userId) {
    return false;
  }

  if (isLegacyAuth()) {
    return Boolean(localStorage.getItem(LOCAL_STORAGE.TOKEN));
  }

  const kc = getKeycloakInstance();
  return Boolean(kc?.authenticated);
};

/**
 * Check if user is authenticated and has valid token
 * @returns {boolean} - true if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const loginStatus = getCookieValue(COOKIES.LOGIN_STATUS);
  const userId = localStorage.getItem(LOCAL_STORAGE.USER_ID);

  if (loginStatus !== LOGGED_IN_STATUS || !userId) {
    return false;
  }

  if (isLegacyAuth()) {
    return Boolean(localStorage.getItem(LOCAL_STORAGE.TOKEN));
  }

  const kc = getKeycloakInstance();
  return Boolean(kc?.authenticated);
};
